import { FC, useState, useEffect } from 'react';
import { Store, Clock, ShoppingCart as ShoppingCartIcon, Smartphone, X, Trash2, LayoutGrid, Sandwich, Coffee, Plus, Cookie, IceCream2 as IceCream, Salad, Pizza, UtensilsCrossed, ChefHat, Package, Truck } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { ProductGrid } from './components/ProductGrid';
import { ShoppingCart } from './components/ShoppingCart';
import { AppOrders } from './components/AppOrders';
import { MobileCartSheet } from './components/MobileCartSheet';
import { CheckoutModal } from './components/CheckoutModal';
import { PresentationSelector } from './components/PresentationSelector';
import { ComplementSelector } from './components/ComplementSelector';
import { ConsiderationSelector } from './components/ConsiderationSelector';
import { ReceiptTypeSelector } from './components/ReceiptTypeSelector';
import { SalesVoidModal } from './components/SalesVoidModal';
import { CashRegisterClosedOverlay } from './components/CashRegisterClosedOverlay';
import { TicketReceiptModal } from './components/TicketReceiptModal';
import { CashRegisterStatusIndicator } from './components/CashRegisterStatusIndicator';
import { ShiftChangeWarningModal } from '@/components/ShiftChangeWarningModal';
import { useProducts } from './hooks/useProducts';
import { useCategories } from './hooks/useCategories';
import { useCart } from './hooks/useCart';
import { useCashRegisterStatus } from './hooks/useCashRegisterStatus';
import { mockAppOrders } from './mocks/products.mock';
import { posService } from '@/services/pos.service';
import { getLocationDetails } from '@/services/authService';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { PaymentMethod, CheckoutData, Product, AppOrder, OrderStatus, SelectedComplement, Consideration, SelectedConsideration, ReceiptType } from './types/pos.types';

export const POSPage: FC = () => {
  const { user, selectedLocation } = useAuth();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [currentShift, setCurrentShift] = useState<string>('MAÑANA');
  const [isReceiptTypeSelectorOpen, setIsReceiptTypeSelectorOpen] = useState(false);
  const [selectedReceiptType, setSelectedReceiptType] = useState<ReceiptType>('boleta');
  const [selectedDiningOption, setSelectedDiningOption] = useState<string>('');
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [isCartSheetOpen, setIsCartSheetOpen] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod>('efectivo');
  const [isPresentationSelectorOpen, setIsPresentationSelectorOpen] = useState(false);
  const [selectedProductForPresentation, setSelectedProductForPresentation] = useState<Product | null>(null);
  const [isComplementSelectorOpen, setIsComplementSelectorOpen] = useState(false);
  const [selectedProductForComplements, setSelectedProductForComplements] = useState<Product | null>(null);
  const [isConsiderationSelectorOpen, setIsConsiderationSelectorOpen] = useState(false);
  const [selectedProductForConsiderations, setSelectedProductForConsiderations] = useState<Product | null>(null);
  const [showAppOrders, setShowAppOrders] = useState(false);
  const [appOrders, setAppOrders] = useState<AppOrder[]>(mockAppOrders);
  const [isSavingSale, setIsSavingSale] = useState(false);
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [showShiftChangeWarning, setShowShiftChangeWarning] = useState(false);
  const [shiftWarningDismissed, setShiftWarningDismissed] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [ticketData, setTicketData] = useState<any>(null);

  // Hooks para manejo de estado
  const {
    products,
    loading: productsLoading,
    error: productsError,
    searchQuery,
    selectedCategory,
    showDeliveryOnly,
    setSearchQuery,
    setSelectedCategory,
    setShowDeliveryOnly
  } = useProducts();

  const {
    categories,
    loading: categoriesLoading,
    error: categoriesError
  } = useCategories();

  const {
    items,
    totals,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart
  } = useCart();

  // Hook para verificar estado de caja registradora
  const {
    status: cashRegisterStatus,
    loading: cashRegisterLoading,
    error: cashRegisterError,
    checkStatus: recheckCashRegisterStatus
  } = useCashRegisterStatus();


  // Obtener turno actual de la ubicación
  const fetchCurrentShift = async () => {
    try {
      const locationId = selectedLocation?.id || user?.locationId;
      if (locationId) {
        const response = await getLocationDetails(locationId);
        if (response.success && response.data.turnoActual) {
          setCurrentShift(response.data.turnoActual);
        }
      }
    } catch (error) {
      console.warn('Error fetching current shift:', error);
      // Mantener el turno por defecto
    }
  };

  // Actualizar hora cada minuto y obtener turno inicial
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    // Obtener turno inicial
    fetchCurrentShift();
    
    // Actualizar turno cada 5 minutos
    const shiftTimer = setInterval(() => {
      fetchCurrentShift();
    }, 300000); // 5 minutos

    return () => {
      clearInterval(timer);
      clearInterval(shiftTimer);
    };
  }, [selectedLocation?.id, user?.locationId]);

  // Detectar cambio de turno y mostrar modal
  useEffect(() => {
    const currentCashShift = cashRegisterStatus.shift;
    const actualShift = cashRegisterStatus.actualShift;

    // Mostrar modal si hay cambio de turno, la caja está abierta y no se ha descartado
    if (
      cashRegisterStatus.isOpen &&
      currentCashShift &&
      actualShift &&
      currentCashShift !== actualShift &&
      actualShift !== 'CERRADO' &&
      !shiftWarningDismissed
    ) {
      setShowShiftChangeWarning(true);
    }
  }, [cashRegisterStatus.isOpen, cashRegisterStatus.shift, cashRegisterStatus.actualShift, shiftWarningDismissed]);

  const handleDismissShiftWarning = () => {
    setShowShiftChangeWarning(false);
    setShiftWarningDismissed(true);
  };

  const handleCheckout = (paymentMethod: PaymentMethod) => {
    // Verificar si la caja está abierta antes de proceder al checkout
    if (!cashRegisterStatus.isOpen) {
      alert('❌ No se puede realizar la venta.\n\nLa caja registradora está cerrada. Necesitas abrir la caja antes de poder realizar ventas.');
      return;
    }

    setSelectedPaymentMethod(paymentMethod);
    setIsReceiptTypeSelectorOpen(true); // Abrir selector de tipo de comprobante primero
  };

  const handleReceiptTypeSelect = (receiptType: ReceiptType, diningOption: string) => {
    setSelectedReceiptType(receiptType);
    setSelectedDiningOption(diningOption);
    setIsReceiptTypeSelectorOpen(false);
    setIsCheckoutOpen(true); // Ahora abrir modal de checkout
  };

  const handleReceiptTypeSelectorClose = () => {
    setIsReceiptTypeSelectorOpen(false);
  };

  const handleCheckoutConfirm = async (data: CheckoutData) => {
    try {
      setIsSavingSale(true);
      
      // Preparar datos de la venta
      const saleData = {
        items: items.map(item => ({
          productId: item.product.id,
          cantidad: item.quantity,
          precioUnitario: item.product.price,
          descuento: 0
        })),
        metodoPago: data.paymentMethod,
        comprobante: {
          tipo: data.receiptType.toUpperCase() as 'BOLETA' | 'FACTURA'
        },
        cliente: data.customerName || data.customerDocument ? {
          tipo: data.receiptType === 'factura' ? 'EMPRESA' : 'PERSONA_NATURAL',
          nombre: data.customerName,
          documento: data.customerDocument,
          email: data.customerEmail,
          telefono: data.customerPhone,
          fechaNacimiento: data.customerBirthDate,
          direccion: data.customerAddress
        } : undefined
      };

      console.log('Guardando venta:', saleData);
      
      // Guardar venta en base de datos
      const response = await posService.createSale(saleData);
      
      if (response.success) {
        // Obtener detalles de la ubicación y empresa para el ticket
        let locationAddress = 'Av. Angamos Este 401 Lima, Miraflores';
        let companyName = 'Empresa S.A.C.';
        let companyRuc = '00000000000';
        try {
          const locationDetails = await getLocationDetails(selectedLocation?.id || '');
          if (locationDetails?.success && locationDetails?.data) {
            // Dirección del local (no de la empresa)
            if (locationDetails.data.direccion) {
              locationAddress = locationDetails.data.direccion;
            }
            // Datos de la empresa desde la tabla companies
            if (locationDetails.data.company) {
              companyName = locationDetails.data.company.razonSocial || locationDetails.data.company.name || companyName;
              companyRuc = locationDetails.data.company.ruc || companyRuc;
            }
          }
        } catch (e) {
          console.warn('No se pudo obtener datos del local/empresa:', e);
        }

        // Preparar datos del ticket
        const now = new Date();
        const dateStr = now.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
        const timeStr = now.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit', hour12: false });

        // Mapear método de pago
        const paymentMethodLabels: Record<string, string> = {
          'efectivo': 'Efectivo',
          'tarjeta_credito': 'Tarjeta Crédito',
          'tarjeta_debito': 'Tarjeta Débito',
          'yape': 'Yape',
          'plin': 'Plin',
          'aplicativo': 'Delivery'
        };

        const ticketInfo = {
          companyName: companyName,
          companyRuc: companyRuc,
          companyAddress: locationAddress,
          receiptType: data.receiptType as 'boleta' | 'factura',
          receiptNumber: response.data?.comprobante?.numero || response.data?.numero || 'N/A',
          ticketNumber: response.data?.comprobante?.numero || response.data?.numero || 'N/A',
          date: dateStr,
          time: timeStr,
          items: items.map(item => ({
            quantity: item.quantity,
            name: item.product.name.toUpperCase(),
            price: item.subtotal
          })),
          subtotal: totals.subtotal,
          igv: totals.igv,
          igvPercent: 10.00,
          otherCharges: totals.otherCharges,
          otherChargesPercent: 13.00,
          valorVenta: totals.subtotal - totals.igv,
          total: totals.total,
          paymentMethod: paymentMethodLabels[data.paymentMethod] || data.paymentMethod,
          amountReceived: data.cashReceived || totals.total,
          change: data.changeAmount || 0,
          customerName: data.customerName,
          customerDocument: data.customerDocument,
          documentType: data.receiptType === 'factura' ? 'RUC' : 'DNI'
        };

        setTicketData(ticketInfo);
        // TODO: habilitar ticket cuando esté listo
        // setShowTicketModal(true);

        // Limpiar carrito y cerrar modal de checkout
        clearCart();
        setIsCheckoutOpen(false);
        setIsCartSheetOpen(false);

        // Mostrar alerta de venta exitosa
        alert('Venta realizada con éxito');
      } else {
        throw new Error(response.error || 'Error al guardar la venta');
      }
      
    } catch (error) {
      console.error('Error al guardar venta:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      alert(`❌ Error al guardar la venta:\n${errorMessage}\n\nIntenta nuevamente.`);
      
    } finally {
      setIsSavingSale(false);
    }
  };

  const handleCheckoutCancel = () => {
    setIsCheckoutOpen(false);
  };

  const handleProductSelect = (product: Product) => {
    // Verificar si la caja está abierta antes de agregar productos
    if (!cashRegisterStatus.isOpen) {
      return; // Bloquear acción si la caja está cerrada
    }
    
    setSelectedProductForPresentation(product);
    setIsPresentationSelectorOpen(true);
  };

  const handlePresentationSelect = (product: Product, presentationData: any, quantity: number) => {
    const productWithPresentation = {
      ...product,
      price: presentationData.price,
      presentationId: parseInt(presentationData.id),
      presentationData: presentationData,
      selectedQuantity: quantity // Agregar la cantidad seleccionada
    };

    // Cerrar selector de presentación
    setIsPresentationSelectorOpen(false);
    setSelectedProductForPresentation(null);

    // Verificar si el producto es de categoría 1 (Sanguches) para mostrar complementos/adicionales
    const categoryId = product.categoryId || parseInt(product.category || '0');
    const isSandwich = categoryId === 1;

    if (isSandwich) {
      // Solo mostrar complementos/adicionales para categoría 1 (Sanguches)
      setSelectedProductForComplements(productWithPresentation);
      setIsComplementSelectorOpen(true);
    } else {
      // Para otras categorías, verificar si requiere consideraciones
      if (requiresConsiderations(product)) {
        setSelectedProductForConsiderations(productWithPresentation as Product);
        setIsConsiderationSelectorOpen(true);
      } else {
        // Si no requiere consideraciones, agregar directamente al carrito
        addProductsToCart(productWithPresentation as Product, [], []);
      }
    }
  };

  const handlePresentationSelectorClose = () => {
    setIsPresentationSelectorOpen(false);
    setSelectedProductForPresentation(null);
  };

  const handleComplementConfirm = (mainProduct: Product, selectedComplements: SelectedComplement[]) => {
    // Cerrar modal de complementos
    setIsComplementSelectorOpen(false);
    setSelectedProductForComplements(null);

    // Guardar complementos seleccionados temporalmente en el producto
    const productWithComplements = {
      ...mainProduct,
      selectedComplements: selectedComplements
    };

    // Verificar si el producto requiere consideraciones
    if (requiresConsiderations(mainProduct)) {
      // Abrir modal de consideraciones
      setSelectedProductForConsiderations(productWithComplements as Product);
      setIsConsiderationSelectorOpen(true);
    } else {
      // Si no requiere consideraciones, agregar directamente al carrito
      addProductsToCart(productWithComplements as Product, selectedComplements, []);
    }
  };

  // Función auxiliar para agregar productos al carrito
  const addProductsToCart = (mainProduct: Product, selectedComplements: SelectedComplement[], selectedConsiderations: SelectedConsideration[]) => {
    // Agregar producto principal al carrito con consideraciones
    const quantity = (mainProduct as any).selectedQuantity || 1;
    const productWithConsiderations = {
      ...mainProduct,
      considerations: selectedConsiderations
    };

    for (let i = 0; i < quantity; i++) {
      addToCart(productWithConsiderations);
    }

    // Agregar cada complemento al carrito con su precio mostrado en el modal
    selectedComplements.forEach(complement => {
      const productToAdd = {
        ...complement.product,
        price: complement.product.price,
        presentationData: { name: 'UNI' }
      };

      for (let i = 0; i < complement.quantity; i++) {
        addToCart(productToAdd);
      }
    });
  };

  const handleComplementSelectorClose = () => {
    setIsComplementSelectorOpen(false);
    setSelectedProductForComplements(null);
  };

  const handleConsiderationConfirm = (product: Product, selectedConsiderations: SelectedConsideration[]) => {
    // Obtener los complementos guardados temporalmente
    const selectedComplements = (product as any).selectedComplements || [];

    // Agregar al carrito con consideraciones
    addProductsToCart(product, selectedComplements, selectedConsiderations);

    // Cerrar modal
    setIsConsiderationSelectorOpen(false);
    setSelectedProductForConsiderations(null);
  };

  const handleConsiderationSelectorClose = () => {
    setIsConsiderationSelectorOpen(false);
    setSelectedProductForConsiderations(null);
  };

  // Obtener productos complementos usando el servicio
  const [complementProducts, setComplementProducts] = useState<Product[]>([]);

  const loadComplementProducts = async () => {
    try {
      const response = await posService.getComplements({ isDelivery: showDeliveryOnly });
      if (response.success) {
        setComplementProducts(response.data);
      }
    } catch (error) {
      console.error('Error loading complements:', error);
    }
  };

  useEffect(() => {
    loadComplementProducts();
  }, [showDeliveryOnly]);

  // Obtener consideraciones usando el servicio
  const [considerations, setConsiderations] = useState<Consideration[]>([]);
  const CATEGORIES_WITH_CONSIDERATIONS = [2, 3, 4]; // 2=Bebidas Frías y Jugos, 3=Bebidas Calientes, 4=Jugos

  const loadConsiderations = async () => {
    try {
      const response = await posService.getConsiderations();
      if (response.success) {
        setConsiderations(response.data);
      }
    } catch (error) {
      console.error('Error loading considerations:', error);
    }
  };

  useEffect(() => {
    loadConsiderations();
  }, []);

  // Verificar si un producto requiere consideraciones
  const requiresConsiderations = (product: Product): boolean => {
    const categoryId = product.categoryId || parseInt(product.category || '0');
    return CATEGORIES_WITH_CONSIDERATIONS.includes(categoryId);
  };

  // Funciones para pedidos del aplicativo
  const handleGenerateReceipt = (order: AppOrder) => {
    const receiptData: CheckoutData = {
      paymentMethod: order.paymentMethod || 'efectivo',
      receiptType: 'boleta', // Por defecto boleta para pedidos de app
      customerName: order.customerName,
      customerDocument: order.customerPhone, // Usar teléfono como documento por defecto
    };

    // Simular generación de comprobante
    let message = `📱 Comprobante generado para pedido ${order.orderNumber}\n`;
    message += `Cliente: ${order.customerName}\n`;
    message += `Total: S/ ${order.totals.total.toFixed(2)}\n`;
    message += `Método de pago: ${order.paymentMethod}`;

    alert(message);
    console.log('Generando comprobante para pedido:', order);
  };

  const handleUpdateOrderStatus = (orderId: string, newStatus: OrderStatus) => {
    setAppOrders(prevOrders => 
      prevOrders.map(order => 
        order.id === orderId 
          ? { ...order, status: newStatus }
          : order
      )
    );
    
    const order = appOrders.find(o => o.id === orderId);
    if (order) {
      console.log(`Estado del pedido ${order.orderNumber} actualizado a: ${newStatus}`);
    }
  };

  return (
    <div className="flex flex-col bg-gray-50 h-full">
      {/* Header - Hidden on mobile when using MainLayout */}
      <header className="flex-shrink-0 bg-white border-b px-4 py-1.5 shadow-sm md:block hidden">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Store className="h-5 w-5 text-macartur-primary" />
            <h1 className="text-sm font-bold text-gray-900">Macartur POS</h1>
            <div className="hidden lg:flex items-center text-xs text-gray-600">
              <CashRegisterStatusIndicator
                status={cashRegisterStatus}
                loading={cashRegisterLoading}
                error={cashRegisterError}
              />
            </div>
          </div>

          <div className="hidden md:flex items-center gap-2">
            <Button
              variant={showDeliveryOnly ? "default" : "outline"}
              onClick={() => setShowDeliveryOnly(!showDeliveryOnly)}
              className={`flex items-center gap-2 h-8 px-3 text-xs font-medium transition-all duration-200 active:scale-95 ${
                showDeliveryOnly
                  ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-md'
                  : 'border border-gray-200 hover:border-blue-500 hover:bg-blue-50 text-gray-700 hover:text-blue-600'
              }`}
            >
              <Truck className="h-4 w-4" />
              <span>Delivery</span>
            </Button>
            {/* Botón App Orders oculto temporalmente */}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col gap-0 overflow-hidden min-h-0">
        {showAppOrders ? (
          /* Vista de Pedidos App - Pantalla completa */
          <div className="flex-1 w-full overflow-hidden">
            <AppOrders
              orders={appOrders}
              onGenerateReceipt={handleGenerateReceipt}
              onUpdateStatus={handleUpdateOrderStatus}
              fullWidth={true}
            />
          </div>
        ) : (
          <>
            {/* Categories Bar - Full width across the screen */}
            <div className="flex-shrink-0 px-3 py-1.5 border-b bg-white">
              <div className="flex gap-1.5 sm:gap-2 overflow-x-auto pb-1">
                {categories.map(category => {
                  // Debug log para verificar comparación
                  console.log(`Category: ${category.name}, ID: "${category.id}" (type: ${typeof category.id}), Selected: "${selectedCategory}" (type: ${typeof selectedCategory}), Match: ${selectedCategory === category.id}`);

                  // Función para dividir el nombre en dos líneas
                  const formatCategoryName = (name: string) => {
                    // Si contiene " y ", dividir por esa palabra
                    if (name.toLowerCase().includes(' y ')) {
                      const parts = name.split(/ y /i);
                      return (
                        <>
                          {parts[0]}<br />y {parts.slice(1).join(' y ')}
                        </>
                      );
                    }

                    // Si tiene dos palabras (como "Bebidas Calientes"), dividirlas
                    const words = name.trim().split(/\s+/);
                    if (words.length === 2) {
                      return (
                        <>
                          {words[0]}<br />{words[1]}
                        </>
                      );
                    }

                    return name;
                  };

                  return (
                    <Button
                      key={category.id}
                      variant={selectedCategory === category.id ? 'default' : 'outline'}
                      className={`whitespace-normal flex items-center justify-center gap-1 h-auto py-1 px-2 sm:px-3 text-[10px] sm:text-xs font-medium transition-all duration-200 min-w-[75px] sm:min-w-[90px] ${
                        selectedCategory === category.id
                          ? 'bg-red-500 hover:bg-red-600 text-white shadow-md'
                          : 'bg-red-50 hover:bg-red-100 border-2 border-red-200 hover:border-red-300 text-gray-700'
                      }`}
                      onClick={() => setSelectedCategory(category.id)}
                    >
                      <span className="font-medium text-center leading-tight">
                        {formatCategoryName(category.label || category.name)}
                      </span>
                    </Button>
                  );
                })}
              </div>
            </div>

            {/* Products and Cart Section */}
            <div className="flex-1 flex flex-col md:flex-row gap-0 overflow-hidden">
              {/* Panel de Productos - Full width on mobile, 70% on desktop */}
              <div className="flex-1 md:w-2/3 flex flex-col">
                <ProductGrid
                  products={products}
                  categories={[]} // Categories are now handled above
                  searchQuery={searchQuery}
                  selectedCategory={selectedCategory}
                  loading={productsLoading || categoriesLoading}
                  error={productsError || categoriesError}
                  onProductSelect={handleProductSelect}
                  onSearchChange={setSearchQuery}
                  onCategoryChange={setSelectedCategory}
                  hideCategories={true} // New prop to hide categories in ProductGrid
                />
              </div>

              {/* Panel de Carrito - Hidden on mobile, fixed width on desktop */}
              <div className="hidden md:flex md:w-[310px] lg:w-[380px] flex-shrink-0 flex-col">
                <ShoppingCart
                  items={items}
                  totals={totals}
                  deliveryMode={showDeliveryOnly}
                  onUpdateQuantity={updateQuantity}
                  onRemoveItem={removeFromCart}
                  onClearCart={clearCart}
                  onCheckout={handleCheckout}
                  disabled={!cashRegisterStatus.isOpen}
                  cashRegisterStatus={cashRegisterStatus}
                />
              </div>
            </div>
          </>
        )}
      </main>

      {/* Mobile Cart Button - Fixed bottom right */}
      <Button
        className="fixed bottom-24 right-4 rounded-full w-14 h-14 shadow-lg md:hidden z-40"
        onClick={() => setIsCartSheetOpen(true)}
        data-testid="mobile-cart-button"
      >
        <ShoppingCartIcon className="w-6 h-6" />
        {items.length > 0 && (
          <Badge className="absolute -top-2 -right-2 bg-macartur-secondary text-white min-w-[20px] h-5 flex items-center justify-center text-xs">
            {items.reduce((total, item) => total + item.quantity, 0)}
          </Badge>
        )}
      </Button>

      {/* Mobile Cart Sheet */}
      <MobileCartSheet
        isOpen={isCartSheetOpen}
        onClose={() => setIsCartSheetOpen(false)}
        items={items}
        totals={totals}
        onUpdateQuantity={updateQuantity}
        onRemoveItem={removeFromCart}
        onClearCart={clearCart}
        onCheckout={handleCheckout}
        disabled={!cashRegisterStatus.isOpen}
        cashRegisterStatus={cashRegisterStatus}
      />

      {/* Receipt Type Selector Modal */}
      <ReceiptTypeSelector
        isOpen={isReceiptTypeSelectorOpen}
        onClose={handleReceiptTypeSelectorClose}
        onSelect={handleReceiptTypeSelect}
      />

      {/* Checkout Modal */}
      <CheckoutModal
        isOpen={isCheckoutOpen}
        paymentMethod={selectedPaymentMethod}
        receiptType={selectedReceiptType}
        diningOption={selectedDiningOption}
        total={totals.total}
        isLoading={isSavingSale}
        onClose={handleCheckoutCancel}
        onConfirm={handleCheckoutConfirm}
      />

      {/* Presentation Selector Modal */}
      <PresentationSelector
        isOpen={isPresentationSelectorOpen}
        product={selectedProductForPresentation}
        deliveryOnly={showDeliveryOnly}
        onClose={handlePresentationSelectorClose}
        onSelectPresentation={handlePresentationSelect}
      />

      {/* Complement Selector Modal */}
      <ComplementSelector
        isOpen={isComplementSelectorOpen}
        mainProduct={selectedProductForComplements}
        complements={complementProducts}
        onClose={handleComplementSelectorClose}
        onConfirm={handleComplementConfirm}
      />

      {/* Consideration Selector Modal */}
      <ConsiderationSelector
        isOpen={isConsiderationSelectorOpen}
        product={selectedProductForConsiderations}
        considerations={considerations}
        onClose={handleConsiderationSelectorClose}
        onConfirm={handleConsiderationConfirm}
      />

      {/* Sales Void Modal */}
      <SalesVoidModal
        isOpen={showVoidModal}
        onClose={() => setShowVoidModal(false)}
      />

      {/* Ticket Receipt Modal */}
      <TicketReceiptModal
        isOpen={showTicketModal}
        onClose={() => setShowTicketModal(false)}
        ticketData={ticketData}
      />

      {/* Overlay para caja cerrada - Solo para CAJEROS (positionId = 3) */}
      {user?.positionId === 3 && !cashRegisterLoading && !cashRegisterStatus.isOpen && (
        <CashRegisterClosedOverlay
          locationName={selectedLocation?.name || user?.locationName}
        />
      )}

      {/* Modal de advertencia de cambio de turno */}
      <ShiftChangeWarningModal
        isOpen={showShiftChangeWarning}
        currentShift={cashRegisterStatus.shift || ''}
        actualShift={cashRegisterStatus.actualShift || ''}
        onClose={handleDismissShiftWarning}
      />
    </div>
  );
};